"use client";
// The hooks barrel, not @/app/(resources)/components: that barrel also
// carries the server-rendered GuidePage and its data, which must not end up
// in this client bundle.
import { guideDoneKey, useStoredValues } from "@/app/(resources)/components/guidePage/hooks";
import type { GuideSlug } from "@/app/(resources)/data";
import { ChoicePills, LinkedText, PageSection } from "@/components/marketing";
import { Progress } from "@/components/ui/progress";
import { useState } from "react";
import GuideHubCard, {
  type GuideHubCardData,
} from "../guideHubCard/guideHubCard.component";

export interface GuidePick {
  key: string;
  label: string;
  recommend: GuideSlug[];
  // Copy with links to the recommended guides.
  answer: string;
}

// A hub card's own copy; its title, route and meta come from the registry.
export interface GuideCard {
  slug: GuideSlug;
  text: string;
  points: string[];
}

interface GuideFinderProps {
  pick: {
    heading: string;
    intro: string;
    fallback: string;
    options: GuidePick[];
  };
  guides: {
    heading: string;
    intro: string;
    progressTemplate: string;
    progressNote: string;
    doneBadge: string;
    openLabel: string;
  };
  cards: GuideHubCardData[];
}

// The hub's two linked sections: "What do you want to do?" highlights the
// recommended guides in "Six guides, in a sensible order", whose progress
// bar and Done badges read the flags each guide saves in this browser.
const GuideFinder = ({ pick, guides, cards }: GuideFinderProps) => {
  const initialState = { pickKey: "" };
  const [state, setState] = useState(initialState);
  const doneFlags = useStoredValues(
    cards.map((card) => guideDoneKey(card.progressId)),
  );
  const chosen = pick.options.find((option) => option.key === state.pickKey);
  const doneCount = doneFlags.filter((flag) => flag === "1").length;
  const progressText = guides.progressTemplate
    .replace("{done}", String(doneCount))
    .replace("{total}", String(cards.length));

  return (
    <>
      <PageSection heading={pick.heading} intro={pick.intro} tone="cream" id="pick">
        <div className="grid gap-3.5">
          <ChoicePills
            ariaLabel={pick.heading}
            options={pick.options}
            value={state.pickKey}
            onChange={(pickKey) => setState((state) => ({ ...state, pickKey }))}
          />
          <p aria-live="polite" className="min-h-[3em] text-[1.05rem] font-bold">
            {chosen ? <LinkedText text={chosen.answer} /> : pick.fallback}
          </p>
        </div>
      </PageSection>
      <PageSection heading={guides.heading} intro={guides.intro} tone="tint" id="guides">
        <div className="mb-[18px] grid items-center gap-3.5 md:grid-cols-[auto_minmax(120px,320px)_1fr]">
          <b aria-live="polite" className="text-base">
            {progressText}
          </b>
          <Progress
            value={(doneCount / cards.length) * 100}
            aria-label={progressText}
            className="h-2.5 bg-line"
            indicatorClassName="bg-tone-mint-ink motion-reduce:transition-none"
          />
          <small className="text-sm text-ink-muted">{guides.progressNote}</small>
        </div>
        <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card, index) => (
            <GuideHubCard
              key={card.slug}
              card={card}
              number={index + 1}
              isDone={doneFlags[index] === "1"}
              isRecommended={chosen?.recommend.includes(card.slug) ?? false}
              doneBadge={guides.doneBadge}
              openLabel={guides.openLabel}
            />
          ))}
        </ul>
      </PageSection>
    </>
  );
};

export default GuideFinder;
